/* Small, manual album: move whole hanging prints along the stationary string.
   Extra image URLs live in an inert template and are requested only on navigation. */
(() => {
  if (customElements.get('tsa-photo-album')) return;
  class PhotoAlbum extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.frames = [...this.querySelectorAll('.tsa-print')];
      if (this.frames.length !== 2) return;
      this.photos = this.frames.map(frame => {
        const img = frame.querySelector('img');
        img.draggable = false;
        return {src: img.getAttribute('src'), alt: img.alt};
      });
      this.photos.push(...[...this.querySelector('template').content.querySelectorAll('[data-src]')]
        .map(n => ({src: n.dataset.src, alt: n.dataset.alt || ''})));
      this.index = 0;
      this.slots = this.frames.map(frame => frame.cloneNode(true));
      this.tabIndex = 0;
      this.setAttribute('role', 'group');
      this.setAttribute('aria-roledescription', 'carousel');
      this.setAttribute('aria-label', 'Travel photo album. Swipe or use the left and right arrow keys to browse.');
      this.classList.add('tsa-album--ready');
      this.addEventListener('keydown', e => {
        const target = {ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.photos.length - 2}[e.key];
        if (target === undefined) return;
        e.preventDefault(); this.go(target, 'keyboard');
      }, options);
      this.addEventListener('pointerdown', e => {
        if (e.pointerType === 'touch' || !e.isPrimary || e.button !== 0 || this.busy) return;
        this.drag = {id: e.pointerId, x: e.clientX, y: e.clientY};
        this.setPointerCapture(e.pointerId);
      }, options);
      this.addEventListener('pointerup', e => {
        if (!this.drag || this.drag.id !== e.pointerId) return;
        const dx = e.clientX - this.drag.x, dy = e.clientY - this.drag.y;
        this.drag = null;
        if (this.hasPointerCapture(e.pointerId)) this.releasePointerCapture(e.pointerId);
        if (Math.abs(dx) >= 32 && Math.abs(dx) > Math.abs(dy) * 1.3) this.go(this.index + (dx < 0 ? 1 : -1), 'drag');
      }, options);
      this.addEventListener('pointercancel', () => { this.drag = null; }, options);
      this.addEventListener('lostpointercapture', () => { this.drag = null; }, options);
      // Handle touch directly: lock only a clearly horizontal gesture, leaving
      // vertical scrolling and two-finger gestures to the browser.
      this.addEventListener('touchstart', e => {
        this.touch = !this.busy && e.touches.length === 1
          ? {id: e.touches[0].identifier, x: e.touches[0].clientX, y: e.touches[0].clientY, axis: null}
          : null;
      }, {...options, passive: true});
      this.addEventListener('touchmove', e => {
        if (!this.touch || e.touches.length !== 1) { this.touch = null; return; }
        const point = e.touches[0];
        const dx = point.clientX - this.touch.x, dy = point.clientY - this.touch.y;
        if (!this.touch.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 8) {
          this.touch.axis = Math.abs(dx) > Math.abs(dy) * 1.3 ? 'x' : 'y';
        }
        if (this.touch.axis === 'x' && e.cancelable) e.preventDefault();
      }, {...options, passive: false});
      this.addEventListener('touchend', e => {
        const start = this.touch;
        this.touch = null;
        if (!start || start.axis === 'y') return;
        const point = [...e.changedTouches].find(t => t.identifier === start.id);
        if (!point) return;
        const dx = point.clientX - start.x, dy = point.clientY - start.y;
        if (Math.abs(dx) >= 32 && Math.abs(dx) > Math.abs(dy) * 1.3) this.go(this.index + (dx < 0 ? 1 : -1), 'swipe');
      }, {...options, passive: true});
      this.addEventListener('touchcancel', () => { this.touch = null; }, options);
      this.addEventListener('dragstart', e => e.preventDefault(), options);
      this.addEventListener('wheel', e => {
        if (Math.abs(e.deltaX) < 12 || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();
        if (!this.busy && Date.now() - (this.lastWheel || 0) > 550) {
          this.lastWheel = Date.now(); this.go(this.index + (e.deltaX > 0 ? 1 : -1), 'wheel');
        }
      }, {...options, passive: false});
    }
    async go(target, method = 'keyboard') {
      target = Math.max(0, Math.min(this.photos.length - 2, target));
      if (this.busy || target === this.index) return;
      this.busy = true;
      const direction = target > this.index ? 1 : -1;
      const incoming = this.photos.slice(target, target + 2).map(photo => {
        const img = new Image(); img.className = 'tsa-print__img';
        img.alt = photo.alt; img.draggable = false; img.decoding = 'async'; img.src = photo.src;
        return img;
      });
      try {
        await Promise.all(incoming.map(img => img.decode()));
        if (!this.isConnected) return;
        const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 520;
        const oldFrames = this.frames;
        const geometry = oldFrames.map(frame => {
          const style = getComputedStyle(frame);
          return {left: frame.offsetLeft, top: frame.offsetTop, width: frame.offsetWidth,
            height: frame.querySelector('img').offsetHeight, transform: style.transform};
        });
        const gap = geometry[1].left - geometry[0].left;
        const nextFrames = incoming.map((img, i) => {
          const frame = this.slots[i].cloneNode(true);
          frame.querySelector('img').replaceWith(img);
          this.append(frame);
          return frame;
        });
        const animations = [];
        const timing = {duration, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both'};
        const pose = (g, opacity = 1) => ({left: `${g.left}px`, top: `${g.top}px`,
          width: `${g.width}px`, transform: g.transform, opacity});
        nextFrames.forEach((frame, i) => {
          const previousSlot = target + i - this.index;
          const start = previousSlot >= 0 && previousSlot < 2
            ? geometry[previousSlot]
            : {...geometry[i], left: geometry[i].left + direction * gap};
          const shared = previousSlot >= 0 && previousSlot < 2;
          animations.push(frame.animate([pose(start, shared ? 1 : 0), pose(geometry[i])], timing));
          animations.push(frame.querySelector('img').animate(
            [{height: `${start.height}px`}, {height: `${geometry[i].height}px`}], timing));
        });
        oldFrames.forEach((frame, i) => {
          const stays = this.index + i >= target && this.index + i < target + 2;
          if (stays) frame.style.visibility = 'hidden';
          else animations.push(frame.animate([pose(geometry[i]),
            pose({...geometry[i], left: geometry[i].left - direction * gap}, 0)], timing));
        });
        await Promise.all(animations.map(animation => animation.finished));
        oldFrames.forEach(frame => frame.remove());
        animations.forEach(animation => animation.cancel());
        this.frames = nextFrames;
        // One event per completed advance; failed loads and edge swipes do not count.
        const eventData = {
          method, direction: direction > 0 ? 'next' : 'previous',
          photo_position: target + 1,
          first_interaction: this.classList.contains('tsa-album--discovered') ? 0 : 1
        };
        try {
          Promise.resolve(window.Shopify?.analytics?.publish('tsa_photo_album_browse', eventData)).catch(() => {});
        } catch (_) { /* Analytics must never interrupt the album. */ }
        this.index = target;
        this.classList.add('tsa-album--discovered');
        this.querySelector('[data-album-caption]').setAttribute('aria-hidden', 'false');
        this.querySelector('[data-album-status]').textContent = `Photos ${target + 1} and ${target + 2} of ${this.photos.length}.`;
      } catch (_) {
        // Keep the current pair intact when an extra photo cannot be loaded.
      } finally { this.busy = false; }
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;

    }
  }
  customElements.define('tsa-photo-album', PhotoAlbum);
})();
